/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import { useTokenListByChain } from "hooks/useTokenListByChain";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBalanceForSymbol } from "../../hooks/useBalanceForSymbol";
import { useGetMaxAmountToWrap } from "../../hooks/wrap/useGetMaxAmountToWrap";
import { GlobalStore } from "../../store/global/GlobalStore";
import { Amount } from "../../types/amount";
import { getPriceForTokenByTokenList } from "../../utils/tokenListUtils";
import AmountInput from "./sections/AmountInput";
import { BalanceDetails } from "./sections/BalanceDetails";
import { BalancePercentageOptionsEnum, BalancePercentages } from "./sections/BalancePercentages";
import { TokenInputSection } from "./sections/TokenInputSection";
import { AnimateChangeInHeight } from "components/general/AnimateChangeInHeight";
import { getAmount, handleBalanceOption } from "utils/tradeUtils";
import { OrderStatus } from "types/orderStatus";
import { InteractionStore } from "store/interactions/InteractionStore";
import { AmountLabel, getLabelType } from "./sections/AmountLabel";
import { Hash } from "viem";
import { isAddressEqual } from "../../utils/tokenUtils";
import { useOnClickOutside } from "usehooks-ts";
import { useTokenlistMetadataByChain } from "../../hooks/queries/tokens-queries";

export interface TradeLegTakerProps {
  tokenAddress: Hash;
  showPlusButton: boolean;
  isWaitingToAddToken: boolean;
  setIsWaitingToAddToken: React.Dispatch<React.SetStateAction<boolean>>;
  setIsChangingToken: React.Dispatch<React.SetStateAction<Hash | undefined>>;
  isExpanded: boolean;
  onFocus: (id: Hash) => void;
  onCollapse: (id: Hash) => void;
}

export const TradeLegTaker: React.FC<TradeLegTakerProps> = ({
  tokenAddress,
  showPlusButton,
  isWaitingToAddToken,
  setIsWaitingToAddToken,
  setIsChangingToken,
  isExpanded,
  onFocus,
  onCollapse,
}) => {
  const tokenList = useTokenListByChain();
  const tradeLegRef = useRef<HTMLDivElement>(null);
  const [amountInputRef, setAmountInputRef] = useState<HTMLInputElement | null>(null);
  const [balancePercentageActive, setBalancePercentageActive] = useState<
    BalancePercentageOptionsEnum | undefined
  >();
  const { address, quote, network, isSending, takerTokens, orderStatus } = GlobalStore.useState(
    s => ({
      address: s.address,
      quote: s.quote,
      network: s.network,
      isSending: s.isSending,
      takerTokens: s.takerTokens,
      orderStatus: s.orderStatus,
    })
  );
  const { data: tokenListMetadata } = useTokenlistMetadataByChain({ chainId: network.id });

  const handleCollapse = useCallback(() => {
    onCollapse(tokenAddress);
  }, [onCollapse, tokenAddress]);

  useOnClickOutside(tradeLegRef, handleCollapse);

  const tokenListPrice = getPriceForTokenByTokenList(tokenAddress, tokenList);
  const quotePrice = quote?.takerTokens[tokenAddress]?.priceUsd;
  const price = quotePrice ?? tokenListPrice;
  const balance = useBalanceForSymbol(tokenAddress);
  const getMaxAmountToWrap = useGetMaxAmountToWrap();
  const tokenDecimals = tokenList?.[tokenAddress]?.chainInfo.decimals;
  const selectedToken = takerTokens.find(entry => isAddressEqual(entry.tokenAddress, tokenAddress));
  const tradeLegToOpenOnAmountBarClick = InteractionStore.useState(
    s => s.tradeLegToOpenOnAmountBarClick
  );

  useEffect(() => {
    if (isExpanded) {
      amountInputRef?.focus();
      if (amountInputRef) {
        amountInputRef.scrollLeft = 0;
      }
      amountInputRef?.setSelectionRange(0, 0);
    }
  }, [amountInputRef, isExpanded]);

  useEffect(() => {
    if (tradeLegToOpenOnAmountBarClick === tokenAddress) {
      onFocus(tokenAddress);
      InteractionStore.update(s => (s.tradeLegToOpenOnAmountBarClick = undefined));
    }
  }, [amountInputRef, onFocus, tokenAddress, tradeLegToOpenOnAmountBarClick]);

  const amount = useMemo(() => {
    if (tokenAddress == null) return Amount.ZERO;
    return getAmount(tokenAddress, quote, "taker", selectedToken);
  }, [quote, selectedToken, tokenAddress]);

  if (tokenList == null) return null;
  const isTradeAmountLargerThanBalance = selectedToken?.amount?.value.gt(
    balance?.balance?.value ?? 0
  );
  return (
    <Container tabIndex={0} onFocus={() => onFocus(tokenAddress)} ref={tradeLegRef}>
      <TradeRowWrapper
        data-testid={"trade-leg"}
        isInputFocused={isExpanded}
        onClick={() => {
          GlobalStore.update(s => {
            s.orderError = undefined;
          });
          if (isExpanded) return;
          amountInputRef?.focus();
          handleCollapse();
        }}
      >
        <TokenInputAndBalanceContainer>
          <TokenControlContainer>
            <TokenInputSection
              chain={network}
              tokenAddress={tokenAddress}
              showPlusButton={showPlusButton}
              setIsWaitingToAddToken={setIsWaitingToAddToken}
              isWaitingToAddToken={isWaitingToAddToken}
              setIsChangingToken={setIsChangingToken}
              tokenList={tokenList}
              tokenListMetadata={tokenListMetadata}
              hideBalancePercentages={() => handleCollapse()}
            />
            <AmountInput
              ref={ref => setAmountInputRef(ref)}
              setBalancePercentageActive={setBalancePercentageActive}
              tokenAddress={tokenAddress}
              isVisible={isExpanded || balancePercentageActive === undefined}
            />
            <AmountLabel
              amountToDisplay={
                amount.value.gt(0)
                  ? amount.toDisplay(tokenAddress, tokenList)
                  : Amount.ZERO.toString()
              }
              labelType={getLabelType(amount)}
              isVisible={!isExpanded && balancePercentageActive !== undefined}
            />
          </TokenControlContainer>
          {balance != null && (
            <BalanceDetails
              tokenAddress={tokenAddress}
              balance={balance}
              price={price ? price : Amount.ZERO}
              amount={selectedToken ? amount : Amount.ZERO}
              onBalanceClick={() => {
                setTimeout(() => {
                  handleCollapse();
                }, 400);
                setBalancePercentageActive(BalancePercentageOptionsEnum.Max);
                if (!tokenDecimals) {
                  console.error(`Token decimals not found for ${tokenAddress}`);
                  return;
                }
                void handleBalanceOption(
                  tokenAddress,
                  "taker",
                  BalancePercentageOptionsEnum.Max,
                  network,
                  balance,
                  getMaxAmountToWrap,
                  tokenDecimals
                );
              }}
              isBalanceShown={!isSending && address !== ""}
              isUsdValueShown={price?.value.gt(0) ?? false}
              isTradeAmountLargerThanBalance={isTradeAmountLargerThanBalance && address !== ""}
            />
          )}
        </TokenInputAndBalanceContainer>
        {address &&
          isExpanded &&
          balance != null &&
          (orderStatus === OrderStatus.ENTRY || orderStatus === OrderStatus.QUOTING) && (
            <BalancePercentages
              balancePercentageActive={balancePercentageActive}
              handlePercentageClick={balanceOption => {
                setTimeout(() => {
                  onCollapse(tokenAddress);
                }, 400);
                if (!tokenDecimals) {
                  console.error(`Token decimals not found for ${tokenAddress}`);
                  return;
                }
                setBalancePercentageActive(balanceOption);
                void handleBalanceOption(
                  tokenAddress,
                  "taker",
                  balanceOption,
                  network,
                  balance,
                  getMaxAmountToWrap,
                  tokenDecimals
                );
              }}
            />
          )}
      </TradeRowWrapper>
    </Container>
  );
};

// Sub components

const Container = styled.div(() => {
  return { padding: "0px 10px", width: "100%" };
});

const TradeRowWrapper = styled(AnimateChangeInHeight)<{
  isInputFocused: boolean;
}>`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 4px;
  border-radius: 24px;
  padding: 0px 14px;
  overflow: hidden;
  background: ${props =>
    props.isInputFocused ? props.theme.surface.main.tertiary.default : "none"};
`;

const TokenInputAndBalanceContainer = styled.div`
  display: flex;
  padding: 14px 2px 16px 0px;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  align-self: stretch;
`;

const TokenControlContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  align-self: stretch;
`;
